import { useCallback, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  BUDGET_BULK_MODE,
  BUDGET_BULK_MODE_OPTIONS,
  getMaxBudgetError,
  getMinBudgetError,
  isBudgetBulkPercentMode,
  MAX_BUDGET_BULK_MODE_OPTIONS,
  normalizeMaxBudgetInput,
  normalizeMinBudgetInput,
} from "../lib/helpers";
import {
  BID_V2_COLORS,
  numberFieldInputProps,
  selectFieldSx,
  textFieldSx,
} from "../lib/bidAutomationStyles";

export const BULK_UPDATE_COLUMN = {
  MIN_BID: "min_bid",
  MAX_BID: "max_bid",
};

const COLUMN_COPY = {
  [BULK_UPDATE_COLUMN.MIN_BID]: {
    title: "Bulk update lower bid cap",
    description: "Choose how to update lower bid cap for all campaigns in this table.",
    fieldLabel: "Lower bid cap",
  },
  [BULK_UPDATE_COLUMN.MAX_BID]: {
    title: "Bulk update upper bid cap",
    description: "Choose how to set upper bid cap for all campaigns in this table.",
    fieldLabel: "Upper bid cap",
  },
};

function BulkInfoTooltip({ title }) {
  if (!title) return null;
  return (
    <Tooltip title={title} arrow placement="top">
      <InfoOutlinedIcon
        sx={{ fontSize: 16, color: BID_V2_COLORS.muted, cursor: "help", verticalAlign: "middle" }}
        aria-label="More information"
      />
    </Tooltip>
  );
}

export function BidBulkColumnHeader({
  label,
  infoTooltip,
  columnType,
  onApply,
  disabled = false,
  justify = "center",
}) {
  return (
    <Stack
      direction="row"
      spacing={0.5}
      alignItems="center"
      justifyContent={justify === "center" ? "center" : "flex-start"}
      component="span"
    >
      <span>{label}</span>
      <BulkInfoTooltip title={infoTooltip} />
      <BidBulkColumnUpdate
        columnType={columnType}
        onApply={onApply}
        disabled={disabled}
      />
    </Stack>
  );
}

export default function BidBulkColumnUpdate({
  columnType,
  onApply,
  disabled = false,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mode, setMode] = useState(BUDGET_BULK_MODE.SET_ABSOLUTE);
  const [value, setValue] = useState("");

  const open = Boolean(anchorEl);
  const copy = COLUMN_COPY[columnType] ?? {};
  const isMaxBid = columnType === BULK_UPDATE_COLUMN.MAX_BID;
  const isMinBid = columnType === BULK_UPDATE_COLUMN.MIN_BID;
  const isPercentMode = isBudgetBulkPercentMode(mode);

  const absoluteError = isMinBid
    ? getMinBudgetError(value)
    : isMaxBid
      ? getMaxBudgetError(value)
      : "";

  const canApply =
    mode === BUDGET_BULK_MODE.SET_ABSOLUTE
      ? value !== "" && !absoluteError
      : value !== "";

  const handleOpen = useCallback(
    (e) => {
      e.stopPropagation();
      if (disabled) return;
      setAnchorEl(e.currentTarget);
    },
    [disabled]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setValue("");
    setMode(BUDGET_BULK_MODE.SET_ABSOLUTE);
  }, []);

  const handleApply = useCallback(() => {
    if (!canApply) return;
    onApply?.({ mode, value });
    handleClose();
  }, [canApply, mode, value, onApply, handleClose]);

  const modeOptions = isMaxBid
    ? MAX_BUDGET_BULK_MODE_OPTIONS.filter(
        (o) => o.value !== BUDGET_BULK_MODE.COPY_CAMPAIGN_BUDGET
      )
    : BUDGET_BULK_MODE_OPTIONS;

  return (
    <>
      <IconButton
        size="small"
        aria-label={copy.title || "Bulk update"}
        disabled={disabled}
        onClick={handleOpen}
        sx={{ p: 0.35 }}
      >
        <EditOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            p: 2,
            width: 280,
            borderRadius: 2,
            border: `1px solid ${BID_V2_COLORS.border}`,
          },
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: "0.875rem", color: BID_V2_COLORS.title, mb: 0.5 }}>
          {copy.title}
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: BID_V2_COLORS.muted, mb: 1.5, lineHeight: 1.4 }}>
          {copy.description}
        </Typography>

        <Stack spacing={1.5} sx={{ mb: 1.5 }}>
          <FormControl fullWidth size="small" sx={selectFieldSx}>
            <InputLabel>Update mode</InputLabel>
            <Select
              label="Update mode"
              value={mode}
              onChange={(e) => {
                setMode(e.target.value);
                setValue("");
              }}
            >
              {modeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="number"
            label={isPercentMode ? "Percent" : copy.fieldLabel}
            value={value}
            onChange={(e) => {
              const next = e.target.value;
              if (isMinBid) setValue(normalizeMinBudgetInput(next));
              else if (isMaxBid) setValue(normalizeMaxBudgetInput(next));
              else setValue(next);
            }}
            error={Boolean(absoluteError) && mode === BUDGET_BULK_MODE.SET_ABSOLUTE}
            helperText={
              mode === BUDGET_BULK_MODE.SET_ABSOLUTE ? absoluteError : undefined
            }
            inputProps={numberFieldInputProps}
            sx={textFieldSx}
          />
        </Stack>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button size="small" onClick={handleClose} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={!canApply}
            onClick={handleApply}
            sx={{ textTransform: "none", boxShadow: "none" }}
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </>
  );
}
